'use client';

import { useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export default function BrowserMultiplicationTable() {
  const [number, setNumber] = useState(7);
  const [upTo, setUpTo] = useState(12);
  const reported = useRef(false);
  const report = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage('multiplication-table', '').catch(() => undefined);
  };

  const n = Math.min(1000, Math.max(0, Math.round(number)));
  const max = Math.min(100, Math.max(1, Math.round(upTo)));
  const grid = Array.from({ length: 12 }, (_, r) =>
    Array.from({ length: 12 }, (_, c) => (r + 1) * (c + 1)),
  );

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Math</span>
        <h2 className="tool-live-title">Multiplication table</h2>
        <p className="tool-live-subtitle">
          Pick a number to see its times table, or use the 12 × 12 chart below. Print it for
          practice.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field">
            <span>Times table of</span>
            <input
              min={0}
              onChange={(e) => {
                report();
                setNumber(Number(e.target.value) || 0);
              }}
              type="number"
              value={number}
            />
          </label>
          <label className="tool-live-field">
            <span>Up to</span>
            <select
              onChange={(e) => {
                report();
                setUpTo(Number(e.target.value));
              }}
              value={upTo}
            >
              {[10, 12, 15, 20, 25, 50, 100].map((v) => (
                <option key={v} value={v}>
                  × {v}
                </option>
              ))}
            </select>
          </label>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => {
                report();
                window.print();
              }}
              type="button"
            >
              Print
            </button>
          </div>
        </section>
        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{n} times table</strong>
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(9rem, 1fr))',
              gap: '0.35rem',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {Array.from({ length: max }, (_, i) => (
              <span key={i}>
                {n} × {i + 1} = <strong>{n * (i + 1)}</strong>
              </span>
            ))}
          </div>
        </section>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{ borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums', width: '100%' }}
        >
          <caption style={{ textAlign: 'left', padding: '0.5rem 0' }}>
            12 × 12 multiplication chart
          </caption>
          <tbody>
            <tr>
              <th scope="col" style={{ padding: '0.3rem' }}>
                ×
              </th>
              {grid[0].map((v) => (
                <th key={v} scope="col" style={{ padding: '0.3rem' }}>
                  {v}
                </th>
              ))}
            </tr>
            {grid.map((row, r) => (
              <tr key={r}>
                <th scope="row" style={{ padding: '0.3rem' }}>
                  {r + 1}
                </th>
                {row.map((v, c) => (
                  <td
                    key={c}
                    style={{
                      padding: '0.3rem',
                      textAlign: 'center',
                      fontWeight: r + 1 === n || c + 1 === n ? 700 : 400,
                      opacity: n >= 1 && n <= 12 && r + 1 !== n && c + 1 !== n ? 0.65 : 1,
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
